<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import type { CtaLoadingCopy } from '../types/site'

const props = defineProps<{
  label: string
  href: string
  /** 缺省时按钮退化为普通链接，不播放加载序列 */
  loading?: CtaLoadingCopy
  /** 打点用的元素名，例如 hero-cta */
  track?: string
}>()

type Phase = 'idle' | 'connecting' | 'checking' | 'entering'

// 时间轴参考自支付类按钮的校验动画：流光 500ms 扫 + 500ms 停，整轮 1s
const STEPS: Array<[Phase, number]> = [['checking', 800], ['entering', 1500]]
const NAVIGATE_AT = 2000

const phase = ref<Phase>('idle')
const timers: number[] = []

const text = computed(() => (
  phase.value === 'idle' || !props.loading ? props.label : props.loading[phase.value]
))

function clearTimers(): void {
  while (timers.length) window.clearTimeout(timers.pop()!)
}

function handleClick(event: MouseEvent): void {
  // 中键、修饰键点击交还给浏览器（新标签页打开等）
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  // 站内锚点或未配置文案时不做加载动画
  if (!/^https?:/i.test(props.href) || !props.loading) return

  event.preventDefault()
  if (phase.value !== 'idle') return

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.location.href = props.href
    return
  }

  phase.value = 'connecting'
  for (const [next, at] of STEPS) {
    timers.push(window.setTimeout(() => { phase.value = next }, at))
  }
  timers.push(window.setTimeout(() => { window.location.href = props.href }, NAVIGATE_AT))
}

onBeforeUnmount(clearTimers)
</script>

<template>
  <a
    class="cta-button"
    :class="{ 'is-loading': phase !== 'idle' }"
    :href="href"
    :aria-busy="phase !== 'idle'"
    :data-track="track"
    @click="handleClick"
  >
    <span class="cta-button__body">
      <!-- 不用 mode="out-in"：串行切换会在中间留出一段两边都不在的空白 -->
        <Transition name="cta-swap">
        <span :key="phase" class="cta-button__text">
          <span>{{ text }}</span>
          <span v-if="phase === 'connecting'" class="cta-button__dots" aria-hidden="true">
            <i></i><i></i><i></i>
          </span>
          <svg v-else-if="phase !== 'idle'" class="cta-button__check" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 12.6 9.6 18.2 20 6.8" />
          </svg>
        </span>
      </Transition>
    </span>
    <span class="cta-button__shimmer" aria-hidden="true"></span>
  </a>
</template>
